// Tile and connection modal content for the MCP section of the integrations page.
// Content only — the copy is still settling, so every field except `id`/`name`/`description`/
// `icon`/`modalTitle`/`contentBlocks` is optional and the modal skips whatever is absent.

// Type-only, so this stays a pure data module with no runtime edge into the icon components.
import type { ColorProp } from "styles/themes";

import type { TokenEndpointAuthMethod } from "lib/hooks/api/auth/mcp/schemas";
import type { AllIconKey } from "lib/icons/untitled_ui/UIcon";

export const MCP_SERVER_URL = "https://api.stotles.com/mcp";

export const MCP_SERVER_NAME = "Stotles";

// Copilot's agent reads this to decide when to call Stotles, so it names the data and the questions
// it answers rather than describing the product. Keep it in step with the Copilot customer setup
// guide, which suggests the same text.
export const MCP_SERVER_DESCRIPTION =
  "Search UK and Ireland public sector procurement data from Stotles: tender and pre-tender notices, contract awards, buyer organisations and their award history, suppliers, frameworks, and buyer-published documents. Use this for any question about public sector contracts, tenders, procurement opportunities, government buyers, or who has won public sector work.";

// Also the Amplitude "Provider" property value.
export type McpProviderId = "claude" | "chatgpt" | "copilot" | "gemini" | "other";

/** Narrows a query-string value, which is whatever the URL happened to carry. */
export function isMcpProviderId(value: unknown): value is McpProviderId {
  return typeof value === "string" && MCP_PROVIDERS.some((provider) => provider.id === value);
}

// A block with no `subtitle` continues the one above it, so a section can carry more than one
// labelled list (Claude's Owner steps, then each member's) without every label becoming a heading.
type McpContentBlock = {
  subtitle?: string;
  text?: string;
  steps?: string[];
};

/**
 * Set on the providers a company member can generate credentials for themselves. Claude and ChatGPT
 * are absent because they register themselves by presenting a URL, so there is nothing to generate.
 */
type McpSelfServe = {
  /**
   * What the credential is registered as. Absent on the generic tile, which has no fixed platform
   * and asks the user what their tool is called. `useCreateMcpClient` appends the company name,
   * so this is the tool alone.
   */
  toolName?: string;
  // Copilot Studio only accepts POST; everything else defaults to Basic. Mirrors the operator CLI's
  // per-platform default. The generic tile lets the user pick, and any credential can switch later.
  tokenEndpointAuthMethod: TokenEndpointAuthMethod;
  /**
   * True when the platform only issues its callback URL after the connection exists, so the
   * credential is registered against a placeholder and the URL is set in a second step.
   */
  callbackUrlIssuedLater?: boolean;
  /** Prefills the callback URL for a platform that always signs in through the same fixed URL. */
  defaultRedirectUri?: string;
};

export type McpProvider = {
  id: McpProviderId;
  name: string;
  description: string;
  selfServe?: McpSelfServe;
  // Multi-colour brand marks (Claude, Gemini, Copilot) bake their own fills in and need no
  // `iconColor`. The monochrome ones draw with currentColor, so they take an explicit colour.
  icon: AllIconKey;
  iconColor?: ColorProp;
  modalTitle: string;
  intro?: string[];
  contentBlocks: McpContentBlock[];
  serverUrl?: string;
  lastVerified?: string;
};

// `lastVerified` is deliberately a literal on each provider rather than a shared constant: each
// provider's docs get re-checked on its own schedule, so the dates will diverge. Only bump the
// one you actually re-verified. Format: "7 August 2026" — keep it consistent across providers,
// it's rendered as-is.

// Order here is display order.
export const MCP_PROVIDERS: McpProvider[] = [
  {
    id: "claude",
    name: "Claude",
    icon: "claude",
    description: "Add Stotles as a custom connector in Claude Pro, Max, Team or Enterprise.",
    modalTitle: "Connect Stotles to Claude",
    intro: ["You'll need a paid Stotles plan and a Claude account."],
    contentBlocks: [
      {
        subtitle: "On Team or Enterprise",
        text: "Only an Owner or Primary Owner can add a custom connector for the organisation. Once they have, each member connects their own Stotles account, so Claude only ever accesses what that person can already see in Stotles.",
      },
      {
        text: "Owner, once per organisation:",
        steps: [
          "Go to Organization settings > Connectors.",
          "Click Add, then Add custom connector.",
          "Enter the name Stotles and the server URL above.",
          "Click Add.",
        ],
      },
      {
        text: "Each member, after that:",
        steps: [
          "Go to Customize > Connectors.",
          'Find Stotles in the list. It will have a "Custom" label.',
          "Click Connect and sign in to your Stotles account.",
          'Enable Stotles in a conversation using the "+" button at the lower left of the chat, then Connectors.',
        ],
      },
      {
        subtitle: "On Pro or Max",
        steps: [
          "Go to Customize > Connectors.",
          'Click "+", then Add custom connector.',
          "Enter the name Stotles and the server URL above.",
          "Click Add, then sign in to Stotles to approve access.",
          'Enable Stotles in a conversation using the "+" button, then Connectors.',
        ],
      },
      {
        subtitle: "On Free",
        text: "Same steps as Pro and Max. Free accounts are limited to one custom connector in total.",
      },
    ],
    serverUrl: MCP_SERVER_URL,
    lastVerified: "7 August 2026",
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    icon: "chatgpt",
    iconColor: "sysTextDefault",
    description: "Set up through developer mode, by you or by an admin for your workspace.",
    modalTitle: "Connect Stotles to ChatGPT",
    intro: [
      "Stotles is added to ChatGPT through developer mode. You'll need a paid Stotles plan. Depending on your ChatGPT plan, you may be able to add Stotles yourself, or an admin may need to set it up and publish it for your workspace. Developer mode is web only, so this isn't available in the ChatGPT mobile apps.",
    ],
    contentBlocks: [
      {
        subtitle: "Adding it yourself",
        text: "If developer mode is available on your account, turn it on in your ChatGPT settings, then add Stotles as a custom MCP connector using the server URL above.",
      },
      {
        subtitle: "Setting it up for a workspace",
        text: "An admin or owner sets Stotles up once and publishes it. Each member then connects their own Stotles account from their Apps settings, where it appears labelled Custom.",
      },
      {
        subtitle: "Instructions for the admin",
        steps: [
          "Enable developer mode: Workspace Settings → Permissions & Roles → Connected Data → Developer mode / Create custom MCP connectors. Each admin or owner turns it on for themselves. Some plans also let admins grant it to specific members.",
          "Create the app: Workspace Settings → Apps → Create. Enter the server URL above, select OAuth 2.0, then click Scan Tools and complete the authorisation prompt. Click Create.",
          "Publish: Workspace Settings → Apps → Drafts → Publish. Once published, the app appears for workspace members in their Apps settings.",
        ],
      },
      {
        subtitle: "Connection details",
        text: "Authentication: OAuth 2.0. Each user signs in to their own Stotles account, so access follows their existing Stotles permissions.",
      },
      {
        subtitle: "Keeping tools up to date",
        text: "ChatGPT uses a frozen snapshot of the available tools. If Stotles adds or changes tools later, an admin needs to update the app. On some plans this is a Refresh in Workspace Settings → Apps; on others the app has to be recreated and republished.",
      },
    ],
    serverUrl: MCP_SERVER_URL,
    lastVerified: "18 August 2026",
  },
  {
    id: "copilot",
    name: "Microsoft 365 Copilot",
    selfServe: {
      toolName: "Copilot Studio",
      tokenEndpointAuthMethod: "client_secret_post",
      callbackUrlIssuedLater: true,
    },
    icon: "microsoft365Copilot",
    description: "Set up in Copilot Studio with a client ID and secret you generate.",
    modalTitle: "Connect Stotles to Microsoft 365 Copilot",
    intro: [
      "Stotles works with Microsoft 365 Copilot, but it can't be added from the Copilot panel in Word, Excel or Teams. Someone with Copilot Studio access needs to set it up and publish it to your organisation.",
    ],
    contentBlocks: [
      {
        subtitle: "Before you start",
        steps: [
          "Generate credentials on the MCP credentials page. You'll get a client ID and secret, plus every other detail the setup asks for. The secret is only shown once, so keep it to hand.",
          "Check that generative orchestration is enabled for the agent. MCP tools are ignored without it.",
        ],
      },
      {
        subtitle: "Instructions to connect",
        steps: [
          "Add the tool: Copilot Studio → open your agent → Tools → Add a tool → New tool → Model Context Protocol. Enter the server name, server description and server URL from your connection details.",
          "Authentication: select OAuth 2.0, then Manual.",
          "Enter the client ID, client secret, authorization URL, token URL, refresh token URL and scopes from your connection details, then click Create.",
          "Copilot Studio gives you a redirect URL once the connection exists. Add that URL to your credential in Stotles, or signing in will fail.",
          "Create the connection, then publish the agent to Microsoft 365 Copilot.",
        ],
      },
      {
        subtitle: "Licensing",
        text: "Copilot Studio is included with Microsoft 365 Copilot licences for internal, employee-facing agents, so no additional licence is usually needed.",
      },
      {
        subtitle: "Connectors Gallery",
        text: "Stotles isn't currently listed in Microsoft's Connectors Gallery, so it can't be added as a federated connector from the Microsoft 365 admin center.",
      },
    ],
    serverUrl: MCP_SERVER_URL,
    lastVerified: "14 August 2026",
  },
  {
    id: "gemini",
    name: "Gemini",
    selfServe: {
      toolName: "Gemini",
      tokenEndpointAuthMethod: "client_secret_basic",
      // Fixed by Google for every Gemini Enterprise MCP connection.
      defaultRedirectUri: "https://vertexaisearch.cloud.google.com/oauth-redirect",
    },
    icon: "gemini",
    description: "Set up in Gemini Enterprise with a client ID and secret you generate.",
    modalTitle: "Connect Stotles to Gemini Enterprise",
    intro: [
      "Stotles works with Gemini Enterprise. It can't be added from the Gemini app itself: an administrator adds it once for your organisation and enables it for the team. The steps below are for Business Edition.",
    ],
    contentBlocks: [
      {
        subtitle: "Before you start",
        steps: [
          "Generate credentials on the MCP credentials page. You'll get a client ID and secret, plus every other detail the setup asks for. The secret is only shown once, so keep it to hand.",
          "Check that you're a team administrator. Only administrators can add an MCP server.",
        ],
      },
      {
        subtitle: "Instructions to connect",
        steps: [
          "Add the server: business.gemini.google → Settings & help → your team → Manage team → Connected apps → Add MCP Server.",
          "Under MCP info, enter the server name and server URL from your connection details, and the server description if it asks for one.",
          "Under Authentication settings, enter the client ID and secret, and any of the other authentication details it asks for, from your connection details.",
          "Save, then enable the connection for your team. Custom MCP servers are disabled by default, so Stotles stays hidden from your team until you turn it on.",
        ],
      },
      {
        subtitle: "Which Gemini this works with",
        text: "Custom MCP servers need a Gemini Enterprise subscription. The Gemini app included with Google Workspace plans can't connect to Stotles.",
      },
    ],
    serverUrl: MCP_SERVER_URL,
    lastVerified: "3 September 2026",
  },
  {
    id: "other",
    name: "Any MCP-compatible AI tool",
    // No fixed name or callback URL: the user says what they're connecting, and a tool that needs
    // a credential at all usually knows its own redirect URI up front.
    selfServe: { tokenEndpointAuthMethod: "client_secret_basic" },
    icon: "integration",
    iconColor: "sysTextDefault",
    description: "Works with any AI tool that supports remote MCP servers.",
    modalTitle: "Connect Stotles to any MCP-compatible AI tool",
    intro: [
      "Stotles works with any AI tool that supports remote MCP servers, not just the ones listed here.",
      "You'll need a paid Stotles plan.",
    ],
    contentBlocks: [
      {
        subtitle: "How to connect",
        steps: [
          "In your AI tool, find the option to add a custom or remote MCP server. The wording varies by tool. If there's no such option, your tool doesn't support remote MCP servers yet.",
          "Paste the Stotles server URL above.",
          "Sign in to Stotles when prompted to approve access.",
        ],
      },
    ],
    serverUrl: MCP_SERVER_URL,
    lastVerified: "7 August 2026",
  },
];
